import React, { useMemo } from "react";
import { makeArrayFromLength, SkeletonLoader } from "oolib";
import { VirtualizedColumn } from "./VirtualizedColumn";
import { distributeItems } from "./utils/distributeItems";
import { StyledGrid, StyledSkeletonContainer } from "./styled";

/**
 * VirtualizedList - A generic virtualized list component supporting both
 * single-column (list) and multi-column (masonry) layouts.
 * 
 * Uses @tanstack/react-virtual for efficient rendering of large lists
 * by only rendering items that are visible in the viewport.
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of items to render
 * @param {Function} props.renderItem - Render function: (item, index) => ReactNode
 * @param {React.RefObject} [props.scrollContainerRef] - Ref to the scrollable parent element (not needed if useWindowScroll=true)
 * @param {boolean} [props.useWindowScroll=false] - Use window/page scroll instead of container scroll
 * @param {number} [props.columns=1] - Number of columns (1 = list, 2+ = masonry)
 * @param {number} [props.gap=16] - Gap between items/columns in pixels
 * @param {number} [props.estimateSize=350] - Estimated item height for virtualization
 * @param {number} [props.overscan=3] - Number of items to render beyond visible area
 * @param {Function} [props.getItemKey] - Custom key generator: (item, index) => string
 * @param {Function} [props.onVisibleRangeChange] - Callback when visible range changes
 * @param {boolean} [props.isLoading=false] - Show loading skeleton state
 * @param {number} [props.loadingSkeletonCount=6] - Number of skeletons to show when loading
 * @param {number} [props.loadingSkeletonHeight=300] - Height of each loading skeleton
 * @param {React.ReactNode} [props.emptyState] - Component to render when data is empty
 * 
 * @example
 * // Single column list with container scroll
 * <VirtualizedList
 *   data={items}
 *   renderItem={(item, index) => <Card key={item.id}>{item.name}</Card>}
 *   scrollContainerRef={scrollRef}
 * />
 * 
 * @example
 * // Multi-column masonry layout with window/page scroll
 * <VirtualizedList
 *   data={items}
 *   renderItem={(item, index) => <Card key={item.id}>{item.name}</Card>}
 *   useWindowScroll={true}
 *   columns={3}
 *   gap={16}
 *   estimateSize={400}
 * />
 */
export const VirtualizedList = ({
  data = [],
  renderItem,
  scrollContainerRef,
  useWindowScroll = false,
  columns = 1,
  gap = 16,
  estimateSize = 350,
  overscan = 3,
  getItemKey,
  onVisibleRangeChange,
  isLoading = false,
  loadingSkeletonCount = 6,
  loadingSkeletonHeight = 300,
  emptyState,
}) => {
  // Distribute items across columns for masonry layout
  const columnData = useMemo(() => {
    if (columns === 1) {
      // For single column, wrap items with originalIndex
      return [data.map((item, index) => ({ item, originalIndex: index }))];
    }
    return distributeItems(data, columns);
  }, [data, columns]);

  // Loading state
  if (isLoading) {
    const skeletonsPerColumn = Math.ceil(loadingSkeletonCount / columns);
    
    return (
      <StyledGrid $columns={columns} $gap={gap}>
        {makeArrayFromLength(columns).map((_, colIndex) => (
          <StyledSkeletonContainer key={colIndex} $gap={gap}>
            {makeArrayFromLength(skeletonsPerColumn).map((_, skelIndex) => (
              <SkeletonLoader
                key={skelIndex}
                style={{ height: `${loadingSkeletonHeight}px`, borderRadius: "0.5rem" }}
              />
            ))}
          </StyledSkeletonContainer>
        ))}
      </StyledGrid>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    if (emptyState) {
      return emptyState;
    }
    return null;
  }

  // Render virtualized columns
  return (
    <StyledGrid $columns={columns} $gap={gap}>
      {columnData.map((items, colIndex) => (
        <VirtualizedColumn
          key={colIndex}
          data={items}
          renderItem={renderItem}
          scrollContainerRef={scrollContainerRef}
          useWindowScroll={useWindowScroll}
          estimateSize={estimateSize}
          overscan={overscan}
          gap={gap}
          getItemKey={getItemKey}
          onVisibleRangeChange={
            // Only report from first column to avoid duplicate callbacks
            colIndex === 0 ? onVisibleRangeChange : undefined
          }
        />
      ))}
    </StyledGrid>
  );
};

export default VirtualizedList;
